<script module>
  // The menu is a place in the history rather than a flag in memory: opening it
  // pushes an entry (same url, marked in `history.state`, which Inertia keeps
  // when it pushes entries of its own), so the browser's back button closes it
  // again - and forward opens it back up.
  //
  // There is no "which entry am I on" in the History API (only `length`, which
  // counts forward entries and other documents too), so the way back is not a
  // `history.go(-n)`: it walks one step at a time until the entry is unflagged.
  // That cannot overshoot, and it copes with however many entries the overlay
  // itself pushed while it was open - those carry the flag too, so they are
  // walked through on the way out.
  const MENU = "menu"

  let open = $state(false)
  // Set while "Back to app" is walking back out to the entry that opened it.
  let closing = false

  // The aside, so a gesture can start from wherever the menu actually is. It
  // lives up here - the one instance assigns it - because the handlers that read
  // it do too, and they are attached once for the page rather than per mount.
  let aside = null

  function onMenuEntry() {
    return window.history.state?.[MENU] === true
  }

  function syncFromHistory() {
    open = onMenuEntry()

    if (closing) {
      if (onMenuEntry()) window.history.back()
      else closing = false
    }
  }

  export function openMenu() {
    if (!open && menuIsAvailable()) {
      window.history.pushState({ ...window.history.state, [MENU]: true }, "")
    }
    open = true
  }

  export function closeMenu() {
    if (!onMenuEntry()) {
      open = false
      return
    }

    closing = true
    window.history.back()
  }

  export function toggle() {
    if (open) closeMenu()
    else openMenu()
  }

  // The button is hidden on desktop, where the user area is a plain sidebar:
  // there is nothing to open, and no history entry to leave behind either. It
  // lives in the layout rather than in here (it belongs to the header row), so
  // the question is asked of the document.
  function menuIsAvailable() {
    const button = document.querySelector("button[aria-label='Menu']")
    return !!button && button.offsetParent !== null
  }

  // --- swiping it in and out -----------------------------------------------
  //
  // The aside is positioned by transform (see the styles), so a gesture can move
  // it frame by frame with the finger and the CSS transition takes over on
  // release. Opening and closing still go through openMenu()/closeMenu(), so the
  // history stays right - the drag itself only moves pixels.
  const SLOP = 10 // px of travel before a touch counts as a drag at all
  const DIRECTION = 1.5 // how far a drag has to beat a scroll to be one
  const FLICK = 0.5 // px/ms: a quick flick settles the other way

  let dragging = $state(false)
  let dragOffset = $state(null) // px left of the open position, until it settles

  let gesture = null

  function onTouchStart(event) {
    if (event.touches.length !== 1 || !menuIsAvailable()) return
    // A drawer over the app owns the screen: no swiping a menu in behind it.
    if (document.querySelector(".modal")) return

    const touch = event.touches[0]
    // Where it is right now, not where the class says: a gesture that starts
    // mid-slide should pick the menu up where it actually is. (Before the layout
    // is up there is no element yet, so fall back to the logical position.)
    const from = aside?.getBoundingClientRect().left ?? (open ? 0 : window.innerWidth)

    gesture = {
      startX: touch.clientX,
      startY: touch.clientY,
      from,
      offset: from,
      velocity: 0,
      time: event.timeStamp,
      moving: false
    }
  }

  function onTouchMove(event) {
    if (!gesture) return

    const touch = event.touches[0]

    if (!gesture.moving) {
      const dx = touch.clientX - gesture.startX
      const dy = touch.clientY - gesture.startY
      if (Math.abs(dx) < SLOP && Math.abs(dy) < SLOP) return
      // Mostly vertical means the page is scrolling, not the menu moving.
      if (Math.abs(dy) * DIRECTION > Math.abs(dx)) {
        gesture = null
        return
      }

      gesture.moving = true
      dragging = true
    }

    // The gesture is ours now: keep the page from scrolling sideways under it.
    if (event.cancelable) event.preventDefault()

    const width = window.innerWidth
    const offset = clamp(gesture.from + touch.clientX - gesture.startX, 0, width)
    const elapsed = event.timeStamp - gesture.time
    if (elapsed > 0) {
      gesture.velocity = (offset - gesture.offset) / elapsed
      gesture.time = event.timeStamp
    }

    gesture.offset = offset
    dragOffset = offset
  }

  function onTouchEnd() {
    const ending = gesture
    gesture = null
    if (!ending?.moving) return

    const progress = 1 - ending.offset / window.innerWidth
    const opening =
      Math.abs(ending.velocity) > FLICK
        ? ending.velocity < 0 // thrown towards open
        : progress > 0.5 // or simply past halfway

    settle(opening)
  }

  function onTouchCancel() {
    const ending = gesture
    gesture = null
    dragging = false
    // Put it back where the state says it belongs, easing from where it is.
    dragOffset = ending?.moving ? (open ? 0 : window.innerWidth) : null
  }

  // Hands the transform back to the class, aimed at where the gesture was
  // heading: the transition animates the rest, and drops the inline value when
  // it lands.
  function settle(opening) {
    dragging = false
    dragOffset = opening ? 0 : window.innerWidth

    if (opening) openMenu()
    else closeMenu()
  }

  function onTransitionEnd(event) {
    if (event.propertyName === "transform" && !dragging) dragOffset = null
  }

  // A px offset is meaningless once the viewport changes (and worse, it would
  // outlive the mobile breakpoint), so let the class take it from here.
  function onResize() {
    if (gesture) return
    dragging = false
    dragOffset = null
  }

  function clamp(value, min, max) {
    return Math.min(Math.max(value, min), max)
  }

  function percent(px) {
    return Math.round((px / window.innerWidth) * 10000) / 100
  }

  // Client only (the module body runs on the server too), and once per page, as
  // there is only ever one of these.
  if (typeof window !== "undefined") {
    syncFromHistory()
    window.addEventListener("popstate", syncFromHistory)

    window.addEventListener("touchstart", onTouchStart, { passive: true })
    // Not passive: the drag has to be able to stop the page scrolling sideways.
    window.addEventListener("touchmove", onTouchMove, { passive: false })
    window.addEventListener("touchend", onTouchEnd)
    window.addEventListener("touchcancel", onTouchCancel)
    window.addEventListener("resize", onResize)
  }
</script>

<script>
  const {
    children
  } = $props()
</script>

<aside
  bind:this={aside}
  class:open
  class:dragging
  style:transform={dragOffset === null ? null : `translateX(${percent(dragOffset)}%)`}
  ontransitionend={onTransitionEnd}
  scroll-region
>
  <section class="!md:hidden">
    <button class="text-gray text-left" onclick={closeMenu}>
      <!-- <button class="p-4 gap-2 flex items-center !justify-start" onclick={back}> -->
        <div class="i-mdi:arrow-left text-2xl"></div>
        Back
      <!-- </button> -->
    </button>
  </section>
 {@render children?.()}
</aside>

<style>
  /* On mobile this is a full-width overlay parked off the right edge, moved by
     transform rather than `left` so a finger can drag it frame by frame (see the
     swipe handlers); from 760px up it is the second column of the layout grid,
     which is why it cannot be wrapped in anything. */
  aside {
    position: fixed;
    top: 0;
    height: 100%;
    left: 0;
    width: 100vw;
    background: #000;
    transform: translateX(100%);
    transition: transform 0.4s cubic-bezier(0.25, 0.1, 0.25, 1);
    will-change: transform;
    overflow: hidden;
    /* z-index: 1; */
  }
  aside.open {
    transform: translateX(0);
  }
  /* While a finger is on it the position is set per frame, so no easing. */
  aside.dragging {
    transition: none;
  }
  @media (min-width: 760px) {
    aside {
      background: none;
      backdrop-filter: none;
      grid-area: aside;
      border-top: 1px solid var(--color-border);
      border-top: none;
      position: sticky;
      max-height: 100dvh;
      width: auto;
      top: 0;
      border-left: 1px solid var(--color-border);
      /* A plain sidebar here: nothing slides, nothing swipes. */
      transform: none;
      transition: none;
      will-change: auto;
    }
  }
</style>
